/**
 * Which app is calling, at which version, and what it understands.
 *
 * Every `/v1` request carries `x-alluzo-client: <app>/<version>/<features>`,
 * for example `mobile/1.9.0/roles` or `web/0.0.0/` (no features yet). The
 * server reads it into the request context. It exists so the server can tell a
 * phone already in people's hands from a newer one: roles, access and Reports
 * to change what the team routes and OS answer, and a build that does not know
 * a new shape must keep getting the old one (web-redesign-02, Decided 12).
 *
 * A feature is added to an app's list only once that app draws it. Nothing on
 * the server acts on the header yet; a missing or unreadable header is read as
 * an old client, never refused.
 */

export const CLIENT_HEADER = 'x-alluzo-client';

/**
 * The features a client can say it understands. Closed, so a typo in an app's
 * list fails typecheck rather than silently turning a door off.
 *
 * - `roles`: roles, per-person access and Reports to (web-redesign-02). OS
 *   offers the new proposal kinds only to a client that sends it.
 * - `set_view`: the app applies a view OS sets on a record's list
 *   (docs/plans/os-live-mode.md 3.1). OS is given the tool only by a client
 *   that sends it, so an older app is never told a view was set that it
 *   cannot show.
 * - `fill_entry`: the app's new-entry page takes what OS fills into it
 *   (web-redesign-10 phase 7). OS is given the tool only by a client that
 *   sends it, and only while that page is open.
 * - `team_cards`: with `roles`, the app draws OS's phase-3 team cards
 *   (docs/plans/roles-parity.md): a person changed, a role removed or copied,
 *   an ask and its answer, an invitation withdrawn or sent again, the
 *   hand-over, and a leave card worded as leaving. OS is given the tools that
 *   make them only by a client that sends it; a build that sends `roles`
 *   alone keeps the team tools and prompts it had before them.
 * - `doc_templates`: the app draws report templates and the documents seat's
 *   cards (docs/plans/report-templates.md, D24). Web only.
 * - `business_details`: the app draws the business detail card, so OS is
 *   offered `read_business_details` and `propose_business_detail`, and its
 *   `propose_fact` refuses a detail (D20, D21). Web only.
 * - `connectors`: the app draws OS's email and rule cards (`email_offer`,
 *   `rule_offer`, docs/plans/connectors-gmail.md Decided 17). OS is given
 *   propose_email and propose_rule only by a client that sends it: the web.
 */
export const CLIENT_FEATURES = [
  'roles',
  'set_view',
  'fill_entry',
  'team_cards',
  'doc_templates',
  'business_details',
  'connectors',
] as const;
export type ClientFeature = (typeof CLIENT_FEATURES)[number];

/** The header, read. `features` holds only the names this build knows. */
export type ClientInfo = {
  /** `mobile`, `web`, or whatever a future client names itself. */
  app: string;
  /** The app's own version, as it sent it. */
  version: string;
  features: ClientFeature[];
};

const PART = /^[a-z0-9][a-z0-9._+-]{0,39}$/i;
/** Long enough for any honest value; a longer one is not ours. */
const MAX_LENGTH = 200;

/** The header value an app sends. */
export const clientHeaderValue = (app: string, version: string, features: readonly ClientFeature[]): string =>
  `${app}/${version}/${features.join(',')}`;

/**
 * The header, read, or null for a missing or malformed one. An unknown feature
 * is dropped rather than refused, so a newer client meeting an older server
 * still says everything this server can use.
 */
export const parseClientHeader = (raw: string | readonly string[] | undefined | null): ClientInfo | null => {
  const value = typeof raw === 'string' ? raw : Array.isArray(raw) ? (raw as readonly string[])[0] : undefined;
  if (value === undefined || value.length > MAX_LENGTH) return null;
  const parts = value.trim().split('/');
  if (parts.length !== 3) return null;
  const [app, version, list] = parts as [string, string, string];
  if (!PART.test(app) || !PART.test(version)) return null;
  const known = CLIENT_FEATURES as readonly string[];
  const features = [
    ...new Set(
      list
        .split(',')
        .map((one) => one.trim())
        .filter((one): one is ClientFeature => known.includes(one)),
    ),
  ];
  return { app: app.toLowerCase(), version, features };
};

/** Whether a read header says the client understands `feature`. Null (an old client) never does. */
export const clientHas = (client: ClientInfo | null, feature: ClientFeature): boolean =>
  client !== null && client.features.includes(feature);

/** Whatever `new Headers()` takes: an object, a list of pairs, or a Headers. */
type HeadersLike = ConstructorParameters<typeof Headers>[0];

/**
 * `init` with the client header set, whatever headers shape it carried. The
 * caller's other headers are kept; a header of the same name is replaced.
 */
export const withClientHeader = <Init extends { headers?: HeadersLike }>(
  init: Init | undefined,
  value: string,
): Init & { headers: Headers } => {
  const headers = new Headers(init?.headers);
  headers.set(CLIENT_HEADER, value);
  return { ...(init as Init), headers };
};
